<script setup lang="ts">
import { computed } from 'vue'
import { CategoryScale, Filler, Chart as ChartJS, LinearScale, LineElement, PointElement } from 'chart.js'
import type { ComponentInstance } from 'vue'
import { Line } from 'vue-chartjs'

const props = defineProps<{
  dataPoints: number[]
  color: string // Expects hex like '#0BCD92'
}>()

type ChartProps = ComponentInstance<typeof Line>['$props']

ChartJS.register(Filler, CategoryScale, LinearScale, PointElement, LineElement)

const chartOptions: ChartProps['options'] = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    x: { display: false },
    y: { display: false },
  },
  plugins: {
    legend: { display: false },
    tooltip: { enabled: false },
  },
  elements: {
    line: {
      tension: 0.4, // Reflecting Figma style
      borderWidth: 1.5, // Reflecting Figma style
    },
    point: {
      radius: 0, // Hide points
    },
  },
}

const chartData = computed<ChartProps['data']>(() => ({
  labels: props.dataPoints.map((_, i) => i),
  datasets: [
    {
      borderColor: props.color,
      data: props.dataPoints,
      fill: true,
      // Vertical gradient based on the chart height
      backgroundColor: (context) => {
        const canvas = context.chart.canvas
        const ctx = context.chart.ctx
        // The quotient determines the fade distance
        const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height / 1.8)

        gradient.addColorStop(0, `${props.color}60`) // 60 is ~38% alpha
        gradient.addColorStop(1, `${props.color}00`) // 00 is 0% alpha

        return gradient
      },
    },
  ],
}))
</script>

<template>
  <div class="sparkline-container">
    <Line :data="chartData" :options="chartOptions" />
  </div>
</template>

<style scoped>
.sparkline-container {
  /* Using 102x33 as seen in your Figma SVG export */
  width: 102px;
  height: 33px;
  display: flex;
  align-items: center;
}
</style>
